import React, { useEffect, useRef, useState } from 'react';
import {
  JSONSchema,
  JSONSchemaProp,
  TAddress,
  TAddressType,
  TError,
  TCustomerType,
  TCustomerContact,
  TCustomerAdd,
  TCustomer, TContactPerson, TParticipantProps,
} from 'models';
import { PhonesAndEmails } from 'app/Pages/Clients/common';
import {
  CustomerProfile,
  renderUserType,
  validateCustomer
} from '../common';
import { AxiosError } from 'axios';
import LoadingOverlay from 'helpers/LoadingOverlay';
import {
  processAPIError,
  validate,
  setRules,
  filterUncommonErrors,
  translateTitleInScheme,
  translate,
  scrollToFirstError,
  getOptionsFromEnum,
  getErrorsByFieldName,
  cloneObject, addCountryCode,
} from 'utils';
import { ADDRESS_VALIDATION_FIELDS, CONTACT_PERSON_VALIDATION_FIELDS } from 'config/itemLists';
import { renderErrors } from 'app/Components/common/Error';
import { shouldShowMoreShow } from 'app/Components/common/ShowMore';
import { DrawerBodyStyled, DrawerCloseIcon, DrawerDataForm, DrawerHeaderRow, DrawerHeaderRowTitle, DrawerSectionTitle } from 'app/Components/common/Drawer/styles';
import { renderControls, scrollElementToBottom } from 'app/Components/common/Drawer';
import { AddressFormPolicyHolder } from 'app/Components/common/AddressForm';
import { addCustomerCommonFields } from 'app/Components/common/Customer';
import { useAuth } from 'context/Auth';
import { Checkbox, Select } from '@insly/qmt-reactjs-ui-lib';
import { DEFAULT_RESIDENT_COUNTRY, DEFAULT_RESIDENT_COUNTRY_CODE } from 'config/api';
import { ContentBodyRow } from 'app/Pages/Quote/Profile/body/styles';
import { SOLE_TRADER_CUSTOMER_TYPE } from 'config/consts';
import { updatePolicyHolderType } from 'app/Pages/Quote/Profile/body/Participants';
import { ADDRESS_DEFAULT, EMAIL_DEFAULT, PHONE_DEFAULT } from 'app/Pages/Clients/consts';
import { contactsValidation, handleChangeParticipant } from 'app/Pages/Quote/Profile/body/Participants/common/utils';
import { ShowMoreForParticipants } from 'app/Pages/QuoteAssistance/Profile/body/Assistance/Drawer/body/styles';
import { ContactsDataBlock, ContactsDataBlockTitle } from '../../../../../styles/common';
import { ButtonAddItem, ContactsControls, ContactsItemsWrapper, IconPlus } from '../../profile/body/styles';
import { ContactPerson } from '../../profile/body/Contacts';
import { contactPersonValidation } from '../../../Quote/Profile/body/Participants/common/utils/contactPersonValidation';

export type THandlePostCustomer = (userForm: TCustomer, onError: (error: AxiosError) => void) => void;

interface IProps {
  customerSchemaContext: Record<string, unknown>,
  handleDrawerClose: () => void,
  handlePostCustomer: THandlePostCustomer,
}

export const contactPersonTemplate = {
  id: '',
  preferred: false,
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
};

type TValue = string | boolean | string[] | number | TCustomerContact[] | TAddress['props'][];

//@TODO: Update Drawer Lib component and make children not required and remove unnecessary <div />
const DrawerAddUser = ({ customerSchemaContext, handleDrawerClose, handlePostCustomer }: IProps) => {
  const [showMore, updateShowMore] = useState(false);

  const bodyEl = useRef<HTMLDivElement>(null);

  return (
    <>
      {renderHeaderRow(translate({ key: 'customer.add.title' }), handleDrawerClose)}
      <DrawerBody
        handlePostCustomer={handlePostCustomer}
        customerSchemaContext={customerSchemaContext}
        bodyEl={bodyEl}
        showMore={showMore}
        updateShowMore={updateShowMore}
      />
      <ShowMoreForParticipants
        show={showMore}
        onClick={() => scrollElementToBottom(bodyEl.current)}
      />
    </>
  );
};

const DrawerBody = ({
  handlePostCustomer,
  customerSchemaContext,
  bodyEl,
  showMore,
  updateShowMore
}: {
  handlePostCustomer: THandlePostCustomer,
  customerSchemaContext: Record<string, unknown>,
  bodyEl: React.RefObject<HTMLDivElement>,
  showMore: boolean,
  updateShowMore: (showMore: boolean) => void,
}) => {
  useEffect(() => {
    if (bodyEl.current !== null) {
      shouldShowMoreShow(bodyEl.current, showMore, updateShowMore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBodyScroll = () => {
    if (bodyEl.current !== null) {
      shouldShowMoreShow(bodyEl.current, showMore, updateShowMore);
    }
  };

  return (
    <DrawerBodyStyled ref={bodyEl} onScroll={onBodyScroll} showMore={true}>
      <UserForm
        handlePostCustomer={handlePostCustomer}
        customerSchema={customerSchemaContext.customerSchema as JSONSchema}
        customerContactsSchema={customerSchemaContext.customerContactsSchema as JSONSchema}
        customerContactPersonsSchema={customerSchemaContext.customerContactPersonSchema as JSONSchema}
        customerAddressSchema={customerSchemaContext.customerAddressSchema as JSONSchema}
      />
    </DrawerBodyStyled>
  );
};

const renderHeaderRow = (title: string, handleDrawerClose: () => void) => (
  <DrawerHeaderRow>
    <DrawerHeaderRowTitle>{title}</DrawerHeaderRowTitle>
    <DrawerCloseIcon icon="close" onClick={handleDrawerClose} />
  </DrawerHeaderRow>
);

const UserForm = ({
  handlePostCustomer,
  customerSchema,
  customerContactsSchema,
  customerContactPersonsSchema,
  customerAddressSchema,
}: {
  handlePostCustomer: THandlePostCustomer,
  customerSchema: JSONSchemaProp,
  customerContactsSchema: JSONSchemaProp,
  customerAddressSchema: JSONSchemaProp,
  customerContactPersonsSchema: JSONSchemaProp,
}) => {
  const { locale } = useAuth();

  const [form, updateForm] = useState<TCustomerAdd>({
    first_name: '',
    last_name: '',
    birthday: '',
    sex: '',
    pesel: '',
    preferred_communication_channel: [],
    preferred_communication_language: '',
    customer_manager_id: '',
    company_name: '',
    company_type: '',
    regon: '',
    pkdCode: '',
    pkdDescription: '',
    contacts: [cloneObject(PHONE_DEFAULT), cloneObject(EMAIL_DEFAULT)],
    addresses: [cloneObject(ADDRESS_DEFAULT)],
    type: 'individual',
    resident: true,
    countryCode: DEFAULT_RESIDENT_COUNTRY_CODE,
    contact_persons: [],
  });

  const [errors, updateErrors] = useState<TError[]>([]);
  const [isTouched, updateIsTouched] = useState(false);

  const [isSubmitting, updateIsSubmitting] = useState(false);
  const schemaProps = customerSchema.properties?.props && customerSchema.properties.props.properties;

  useEffect(() => {
    if (errors?.length) {
      scrollToFirstError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(errors)]);

  const handleChange = (key?: string, value?: TValue) => {
    if (key) {
      updateErrors([]);
      updateIsTouched(true);

      if (key === 'pkdCode' && typeof value === 'string') {
        value = value.split('.').join('');
      }

      const touchedForm = {
        ...form,
        [key]: value,
      };

      if (key === 'resident') {
        if (value) {
          touchedForm.countryCode = DEFAULT_RESIDENT_COUNTRY_CODE;
        } else {
          touchedForm.countryCode = undefined;
        }
      }

      updateForm(touchedForm);
    }
  };

  const handleChangeMultiple = (data: Partial<TCustomerAdd>) => {
    updateForm({
      ...form,
      ...data,
    });
  };

  const handleSaveData = () => {
    updateIsSubmitting(true);

    let errorsUI = validateCustomer({
      customerSchema,
      schemaProps,
      form,
      excludedFields: ['domain', 'customer_status', 'props']
    });

    const addressRequiredFields = [];
    const contactsRequiredFields: string[] = [];
    const contactPersonRequiredFields: string[] = [];

    if (customerContactsSchema.required?.length) {
      contactsRequiredFields.push(...customerContactsSchema.required);
    }

    if (customerAddressSchema.properties && customerAddressSchema.properties.props && customerAddressSchema.properties.props.required?.length) {
      addressRequiredFields.push(...customerAddressSchema.properties.props.required);
    }

    if (customerContactPersonsSchema) {
      contactPersonRequiredFields.push(...CONTACT_PERSON_VALIDATION_FIELDS);
    }

    let contacts: { subtype?: string, type: 'phone' | 'email', value: string }[] = [];
    if (form.contacts?.length) {
      contacts = form.contacts.map(item => ({
        type: item.type,
        value: item.value,
        subtype: item.subtype
      }));
    }

    let addresses: { props: { type: TAddressType[], zip_code?: string, country: string, city: string, street: string, house: string } }[] = [];
    if (form.addresses?.length) {
      addresses = form.addresses.map(item => ({
        props: {
          type: item.type,
          zip_code: item.zip_code,
          city: item.city,
          street: item.street,
          house: item.house,
          room: item.room || undefined,
          county: item.county,
          post: item.post,
          commune: item.commune,
          region: item.region,
          country: (form.type === 'individual' && form.resident === false) ? '' : DEFAULT_RESIDENT_COUNTRY,
        }
      }));
    }

    let contactPersons: {
      preferred: boolean,
      props: {
        first_name: string,
        last_name: string,
        email: string,
        phone: string,
      },
    }[] = [];

    if (form.contact_persons?.length) {
      if (form.contact_persons?.[0].last_name.trim()) {
        contactPersons = form.contact_persons.map(item => ({
          preferred: item.preferred,
          props: {
            first_name: item.first_name,
            last_name: item.last_name,
            email: item.email,
            phone: (item.phone && !item.phone.startsWith(window._env_.DEFAULT_COUNTRY_CODE)) ? addCountryCode(item.phone) : item.phone,
          }
        }));
      } else if (form.contact_persons?.[0].email || form.contact_persons?.[0].phone || form.contact_persons?.[0].first_name) {
        errorsUI.push({
          code: '',
          message: translate({ key: 'error.validation.contact_person.missing_last_name' }),
        });
      }
    }

    if (addresses?.length) {
      addressRequiredFields.forEach((item) => {
        const props = customerAddressSchema.properties?.props.properties;
        let pattern, regExpErrorMessage;

        if (['zip_code'].includes(item) && form.resident === true) {
          pattern = new RegExp(/^\d{2}-\d{3}$/);
          regExpErrorMessage = translate({ key: 'error.regexp_zip' });
        }

        const rules = setRules({
          props,
          item,
          pattern,
          regExpErrorMessage
        });

        const errors = validate({
          field: item,
          fieldName: props && props[item] ? translateTitleInScheme(props[item]) : undefined,
          value: form.addresses[0][item as keyof TAddress['props']],
          rules,
          parentField: 'addresses',
          listIndex: 0,
          uncommonError: ADDRESS_VALIDATION_FIELDS.includes(item)
        });

        if (errors?.length) {
          errorsUI = [
            ...errorsUI,
            ...errors
          ];
        }

      });
    }

    if (contacts?.length) {
      errorsUI = contactsValidation(contacts, contactsRequiredFields, errorsUI, customerContactsSchema.properties);
    }

    if (contactPersons?.length) {
      errorsUI = contactPersonValidation(contactPersons[0].props, contactPersonRequiredFields, errorsUI, customerContactPersonsSchema.properties?.props.properties);
    }

    if (errorsUI?.length) {
      updateErrors(errorsUI);
      updateIsSubmitting(false);
      return;
    }

    let requestObject: TParticipantProps = {
      addresses: [],
      contacts: [],
      contact_persons: [],
      type: form.type,
      props: {
        customer_status: 'active',
      }
    } as TCustomer;

    requestObject = addCustomerCommonFields(form, requestObject as TCustomer, addresses as TAddress[], contacts);

    if (contactPersons.length) {
      requestObject.contact_persons = contactPersons;
    }

    handlePostCustomer(requestObject as TCustomer, (error) => {
      updateErrors(() => processAPIError(error, true) as TError[]);
      updateIsSubmitting(false);
    });

    updateIsTouched(false);
  };

  return (
    <DrawerDataForm>
      {isSubmitting ? <LoadingOverlay /> : null}
      {renderUserType(
        form.company_type === SOLE_TRADER_CUSTOMER_TYPE ? SOLE_TRADER_CUSTOMER_TYPE : form.type,
        customerSchema.properties?.type as JSONSchemaProp,
        (key, value) => updatePolicyHolderType(key, value, handleChangeMultiple),
        customerSchema.properties?.type?.enum as TCustomerType[],
        'type',
        true,
        undefined,
        true
      )}
      <ContentBodyRow>
        {(form.type === 'individual') ? (
          <Checkbox
            name="resident"
            label={translateTitleInScheme(schemaProps?.resident)}
            checked={form.resident}
            handleChange={handleChange}
          />
        ) : null}
        {form.type === 'individual' && form.resident === false ? (
          <Select
            name="countryCode"
            label={translateTitleInScheme(schemaProps?.countryCode)}
            value={form?.countryCode}
            options={getOptionsFromEnum(schemaProps?.countryCode).filter(item => item.key !== DEFAULT_RESIDENT_COUNTRY_CODE) || []}
            handleChange={handleChange}
            searchable={true}
            error={errors?.length ? getErrorsByFieldName('countryCode', errors).join('\n') : ''}
            required={!form.resident}
          />
        ) : null}
      </ContentBodyRow>
      <DrawerSectionTitle>{translate({ key: 'customer.personal_data.title' })}</DrawerSectionTitle>
      <CustomerProfile
        form={form}
        schemaProps={schemaProps as JSONSchema}
        userLocale={locale}
        handleChange={handleChange}
        handleChangeMultiple={handleChangeMultiple}
        errors={errors}
        isClients
      />
      <PhonesAndEmails
        withLabels
        contacts={form.contacts}
        handleChangeForm={handleChange}
        errors={errors}
      />
      <ContactPersons
        item={form.contact_persons?.[0]}
        handleFormChange={(key, value) => handleChangeParticipant(form, updateErrors, updateIsTouched, updateForm, 'contact_persons', [{
          ...form.contact_persons?.[0],
          [key]: value,
          preferred: true,
        } as TContactPerson])}
        onAddContactPerson={() => handleChangeParticipant(form, updateErrors, updateIsTouched, updateForm, 'contact_persons', [{ ...contactPersonTemplate }])}
        schema={customerContactPersonsSchema}
        errors={errors}
        transparent={true}
      />
      <AddressFormPolicyHolder title={translate({ key: 'common.address' })} item={form.addresses[0] as TAddress['props']} handleFormChange={handleChange} schemaProps={customerAddressSchema.properties?.props.properties as JSONSchema} errors={errors} isNotResident={form.type === 'individual' && form.resident === false} />
      {/*{renderCommunication(schemaProps as JSONSchema, form.preferred_communication_channel as TCommunicationChanel[], handleChange)}*/}
      {errors?.length ? renderErrors(filterUncommonErrors(errors)) : null}
      {(isTouched) ? renderControls('list', isTouched, handleSaveData) : null}
    </DrawerDataForm>
  );

};

export const ContactPersons = ({ item, handleFormChange, onAddContactPerson, schema, errors, readOnly, transparent }: {item?: TContactPerson, handleFormChange: (name: keyof TContactPerson, value: (string | boolean), index: number) => void, onAddContactPerson: () => void, schema: JSONSchemaProp, errors: TError[], readOnly?: boolean, transparent?: boolean}) =>(
  <ContactsDataBlock>
    <ContactsDataBlockTitle>
      {translate({ key: 'customer.contact.contact_person' })}
    </ContactsDataBlockTitle>
    <ContactsItemsWrapper>
      {item ? (
        <ContactPerson
          item={item}
          index={0}
          handleChange={handleFormChange}
          contactPersonSchema={schema}
          errors={errors}
          preferredByDefault={true}
        />
      ) : (
        <ContactsControls flexPosition="start">
          <ButtonAddItem transparent={transparent} preset="secondary" onClick={onAddContactPerson} disabled={readOnly}>
            <>
              {translate({ key: 'customer.contact.add_contact_person' })} <IconPlus icon="plus" />
            </>
          </ButtonAddItem>
        </ContactsControls>
      )}
    </ContactsItemsWrapper>
  </ContactsDataBlock>
);


export default DrawerAddUser;
